import { usePrivy } from '@privy-io/react-auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useSyncExternalStore,
} from 'react';

import { useGetUserProfile } from '@/api/user-dashboard/queries';
import {
  fetchLatestWalletAccess,
  walletAccessQueryOptions,
  type WalletAccessResponse,
} from '@/api/wallet/wallet-access';
import { FeatureFlags, useFeatureFlag } from '@/global-hooks/use-feature-flag-enabled';

import {
  checkGrantIdentity,
  GrantIdentity,
  type PrivyBinding,
  resolvePrivyBinding,
} from './privy-binding';
import { forgetRememberedPasskey } from './remembered-wallet-passkey';
import { useWalletAccessFlow } from './use-wallet-access-flow';
import {
  useWalletActivity,
  useWalletBoundRestore,
  useWalletGrantExpiry,
  useWalletIdentityLock,
  useWalletOwnerLifecycle,
} from './use-wallet-access-lifecycle';
import { useEndPrivySession, useWalletLock } from './use-wallet-lock';
import { useWalletOperations } from './use-wallet-operations';
import { type WalletOperation } from './wallet-operation';
import { type WalletUnlockGrant, walletUnlockSession } from './wallet-unlock-session';

export type WalletAccessContextValue = {
  access: WalletAccessResponse | undefined;
  isReady: boolean;
  isLoading: boolean;
  isUnlocked: boolean;
  isBusy: boolean;
  error: string | null;
  setUp: () => Promise<boolean>;
  unlock: () => Promise<boolean>;
  unlockWithAnotherPasskey: () => Promise<boolean>;
  lock: () => Promise<void>;
  recordActivity: () => boolean;
  beginOperation: () => WalletOperation;
  getWalletAccessToken: () => Promise<string>;
  refreshAccess: () => Promise<WalletAccessResponse>;
};

const ACCESS_UNAVAILABLE = 'Wallet information is unavailable.';

const WalletAccessContext = createContext<WalletAccessContextValue | null>(null);

export const WalletAccessProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useGetUserProfile();
  const ownerId = user?.id ?? null;
  const walletEnabled = useFeatureFlag(FeatureFlags.CRYPTO_WALLET);
  const gammaUserId = walletEnabled ? ownerId : null;
  const privy = usePrivy();
  const accessQuery = useQuery({
    ...walletAccessQueryOptions(gammaUserId ?? ''),
    enabled: gammaUserId !== null,
  });
  const access = accessQuery.data;
  const binding = resolvePrivyBinding(privy, access);
  const grant = useSyncExternalStore(
    walletUnlockSession.subscribe,
    walletUnlockSession.getSnapshot,
  );
  const endPrivySession = useEndPrivySession();
  const lock = useWalletLock(endPrivySession);
  useWalletOwnerLifecycle(ownerId, walletEnabled, privy.ready, lock);
  useWalletGrantExpiry(grant, endPrivySession);
  useWalletIdentityLock(grant, binding, lock);
  useWalletBoundRestore(binding);
  useWalletActivity();
  const refreshAccess = useRefreshWalletAccess(gammaUserId);
  const flow = useWalletAccessFlow({
    gammaUserId,
    privyReady: privy.ready,
    lock,
    readAccess: refreshAccess,
  });
  const operations = useWalletOperations(lock);
  const unlockWithAnotherPasskey = useUnlockWithAnotherPasskey(access, flow.unlock);

  const value: WalletAccessContextValue = {
    access,
    isReady: access !== undefined && privy.ready,
    isLoading: accessQuery.isPending,
    isUnlocked: isGrantActive(grant, gammaUserId, binding),
    isBusy: flow.isBusy,
    error: flow.error ?? (accessQuery.isError ? ACCESS_UNAVAILABLE : null),
    setUp: flow.setUp,
    unlock: flow.unlock,
    unlockWithAnotherPasskey,
    lock,
    ...operations,
    refreshAccess,
  };
  return <WalletAccessContext.Provider value={value}>{children}</WalletAccessContext.Provider>;
};

export const useWalletAccess = (): WalletAccessContextValue => {
  const value = useContext(WalletAccessContext);

  if (!value) {
    throw new Error('Wallet access provider is unavailable.');
  }

  return value;
};

function useRefreshWalletAccess(gammaUserId: string | null) {
  const queryClient = useQueryClient();
  return useCallback(
    () => fetchLatestWalletAccess(queryClient, gammaUserId ?? ''),
    [gammaUserId, queryClient],
  );
}

function useUnlockWithAnotherPasskey(
  access: WalletAccessResponse | undefined,
  unlock: () => Promise<boolean>,
): () => Promise<boolean> {
  const privyUserId = access?.privyUserId;

  return useCallback(() => {
    if (privyUserId) {
      forgetRememberedPasskey(privyUserId);
    }

    return unlock();
  }, [privyUserId, unlock]);
}

function isGrantActive(
  grant: WalletUnlockGrant | null,
  gammaUserId: string | null,
  binding: PrivyBinding,
): boolean {
  return (
    grant !== null &&
    grant.gammaUserId === gammaUserId &&
    checkGrantIdentity(binding, grant) === GrantIdentity.Verified &&
    walletUnlockSession.isUnlocked(Date.now())
  );
}
